import styled from "styled-components";
import { usePage } from "@inertiajs/react";
import { Users, MapPin, Leaf, Award } from "lucide-react";

import Media from "../_kit/Media";
import Card from "../_kit/Card";
import {
  Section,
  SectionHeader,
  Rule,
  Lede,
  Heading,
  Grid,
  Eyebrow,
  Container,
} from "../_kit/Section";
import { useLandingLanguage } from "../../../contexts/landing-language";
import {
  weight,
  type,
  space,
  radius,
  media,
  leading,
  font,
  color,
} from "../../../styles/tokens";

const FEATURE_ICONS = [Leaf, MapPin, Award, Users];

const Split = styled.div`
  display: grid;
  grid-template-columns: ${(props) => (props.$withImage ? "1fr 1fr" : "1fr")};
  gap: clamp(32px, 5vw, 72px);
  align-items: center;
  margin-bottom: ${space[9]};

  ${media.lg`
    grid-template-columns: 1fr;
  `}
`;

const Copy = styled.div`
  max-width: 56ch;

  p {
    margin: 0 0 ${space[4]};
    font-size: ${type.base};
    line-height: ${leading.relaxed};
    color: ${color.ink700};
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

const CopyHeading = styled.h3`
  margin: 0 0 ${space[4]};
  font-family: ${font.display};
  font-size: ${type.h3};
  font-weight: ${weight.regular};
  line-height: ${leading.snug};
  color: ${color.ink};
`;

const FeatureCard = styled(Card)`
  padding: ${space[5]};
  gap: ${space[2]};
  background: transparent;
  border-color: transparent;
  border-top: 2px solid ${color.gold};
  border-radius: 0;
`;

const FeatureIcon = styled.span`
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  margin-bottom: ${space[1]};
  border-radius: ${radius.sm};
  color: ${color.primary};
`;

const FeatureTitle = styled.h3`
  margin: 0;
  font-size: ${type.h4};
  font-weight: ${weight.semibold};
  color: ${color.ink};
`;

const FeatureBody = styled.p`
  margin: 0;
  font-size: ${type.sm};
  line-height: ${leading.relaxed};
  color: ${color.ink500};
`;

const About = (props) => {
  const { t } = useLandingLanguage();
  const { landing_content: landingContent } = usePage().props;
  const copy = t.about;

  const image = landingContent?.about_image || null;
  const features = [
    copy.features.eco,
    copy.features.location,
    copy.features.service,
    copy.features.community,
  ];

  return (
    <Section id="about">
      <Container>
        <SectionHeader>
          <Eyebrow>{copy.subtitle}</Eyebrow>
          <Heading>
            {copy.title} <strong>Hebe Hostel</strong>
          </Heading>
          <Lede>{copy.description}</Lede>
        </SectionHeader>

        <Split $withImage={Boolean(image)}>
          {image ? (
            <Media
              source={image}
              alt={image.alt_text || ""}
              ratio="4 / 3"
              radius={radius.md}
              sizes="(max-width: 1024px) 100vw, 50vw"
            />
          ) : null}
          <Copy>
            <Rule />
            <CopyHeading>{copy.contentHeading}</CopyHeading>
            <p>{copy.p1}</p>
            <p>{copy.p2}</p>
          </Copy>
        </Split>

        <Grid $cols={4}>
          {features.map((feature, index) => {
            const Icon = FEATURE_ICONS[index];
            return (
              <FeatureCard key={feature.title}>
                <FeatureIcon>
                  <Icon size={26} aria-hidden="true" />
                </FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureBody>{feature.desc}</FeatureBody>
              </FeatureCard>
            );
          })}
        </Grid>
      </Container>
    </Section>
  );
};

export default About;
